import dayjs from 'dayjs';
import { get } from 'lodash';
import { Spinner } from 'flowbite-react';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import { HiCheck, HiOutlineBriefcase, HiOutlineLockClosed, HiOutlineCalendar, HiOutlineExternalLink, HiOutlineLocationMarker } from 'react-icons/hi';
import useRazorpay, { RazorpayOptions } from 'react-razorpay';
import { useMutation, useQueryClient } from 'react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { cancelDrivePayment, completeDrivePayment, createDrivePayment, validateDriveCoupon, ValidateDriveCouponResponse } from 'src/apis/drive';
import DriveCard from 'src/components/placementDrives/DriveCard';
import { MobileHeader, CompanyLogo, DateBadge, formatPositionTitle, MobileActionBar, PrimaryButton, StatusPill, DRIVE_FONT_STYLE, useDriveFont } from 'src/components/placementDrives/shared';
import Container from 'src/components/shared/Container';
import { APP_ENDPOINT, RAZORPAY_APP_ID } from 'src/config/env.config';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetMyDriveStatus } from 'src/hooks/drives.hooks';
import { GetMyDriveStatusAPIResponse, JobseekerDrivePosition } from 'types';

export const ApplicationList = ({ applications }: { applications: JobseekerDrivePosition[] }) => (
	<div className="rounded-[20px] border border-[#EEEEF5] bg-white dark:bg-[#1F2937] divide-y divide-[#EEEEF5] overflow-hidden">
		{applications.map(position => (
			<div key={position._id} className="flex items-start tsm:items-center gap-3 tsm:gap-4 p-4 tsm:p-5">
				<CompanyLogo name={position.company.name} logo={position.company.logo} url={position.company.url} size={48} />
				<div className="flex-1 min-w-0">
					<p className="text-[17px] font-[700] text-[#111E2C] dark:text-white">{position.company.name}</p>
					<p className="text-[14px] text-[#555770]">{formatPositionTitle(position.title)}</p>
					<div className="mt-2 tsm:hidden">
						<StatusPill status={position.applicationStatus} />
					</div>
				</div>
				<div className="hidden tsm:block">
					<StatusPill status={position.applicationStatus} />
				</div>
			</div>
		))}
	</div>
);

// Single line with an ellipsis; tapping it (e.g. on mobile, where there is no hover title) shows the full text.
const TapToExpandText = ({ text, className = '' }: { text: string; className?: string }) => {
	const [expanded, setExpanded] = React.useState(false);
	return (
		<p
			className={`${className} cursor-pointer ${expanded ? 'break-words' : 'truncate'}`}
			title={text}
			role="button"
			tabIndex={0}
			aria-expanded={expanded}
			onClick={() => setExpanded(prev => !prev)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					setExpanded(prev => !prev);
				}
			}}
		>
			{text}
		</p>
	);
};

const SidePanel = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<aside className="h-fit rounded-[20px] bg-[#F6F6FA] dark:bg-[#1F2937] p-6">
		<h3 className="text-[18px] font-[700] text-[#111E2C] dark:text-white">{title}</h3>
		{children}
	</aside>
);

const UnderReview = ({ data }: { data: GetMyDriveStatusAPIResponse }) => {
	const navigate = useNavigate();
	const { drive } = data;
	const { isEnabled: feeEnabled, amount: feeAmount } = drive.registrationPayment;
	const steps = [
		'Companies review your resume',
		feeEnabled ? `Get shortlisted? Pay a ₹${feeAmount} seat deposit to confirm your seat` : 'Get shortlisted? Your seat is confirmed automatically',
		'Attend the drive and meet the companies',
	];
	return (
		<div className="grid gap-8 lg:grid-cols-[1fr_380px]">
			<div>
				<div className="flex flex-col items-center text-center gap-4 pt-2 lg:flex-row lg:gap-5 lg:pt-0 lg:text-left">
					<div className="dp-pop w-[72px] h-[72px] shrink-0 rounded-full bg-[#E6F6EC] text-[#1E7A44] flex items-center justify-center text-[32px]">
						<HiCheck />
					</div>
					<div>
						<h1 className="text-[28px] leading-[32px] lg:text-[36px] lg:leading-[40px] tracking-[-0.03em] font-[800] text-[#111E2C] dark:text-white">Application submitted</h1>
						<p className="mt-1.5 lg:mt-0 text-[16px] text-[#555770]">Companies will respond by <span className="font-[700] text-[#111E2C] dark:text-white">{dayjs(drive.applicationCloseDate).format('D MMM')}</span></p>
					</div>
				</div>
				<div className="dp-rise mt-6" style={{ animationDelay: '0.12s' }}>
					<ApplicationList applications={data.applications} />
				</div>
				{data.canAddMorePositions ? (
					<button
						onClick={() => navigate(`/placement-drives/${drive._id}/apply`)}
						className="dp-rise mt-5 w-full lg:w-auto rounded-[12px] bg-white border border-[#EEEEF5] px-6 py-3.5 lg:py-3 font-[600] text-[#111E2C]"
						style={{ animationDelay: '0.2s' }}
					>
						Add more companies
					</button>
				) : null}
			</div>
			<div className="dp-rise" style={{ animationDelay: '0.28s' }}>
			<SidePanel title="What happens next">
				<ol className="mt-4 space-y-4">
					{steps.map((step, index) => (
						<li key={step} className="flex gap-3 text-[15px] text-[#555770] dark:text-[#B0B8C4]">
							<span className="w-6 h-6 rounded-full bg-white text-[#111E2C] text-[12px] font-[700] flex items-center justify-center shrink-0">
								{index + 1}
							</span>
							{step}
						</li>
					))}
				</ol>
			</SidePanel>
			</div>
		</div>
	);
};

// Full-screen payment feedback: a spinner while the payment is verified, then the confirmed state.
// Whole rupees stay plain (999); a discounted amount always shows two decimals (699.30).
const formatRupees = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

const PaymentOverlay = ({ state, amount, driveDate }: { state: 'processing' | 'done'; amount: number; driveDate: Date | string }) => (
	<div
		role="status"
		aria-live="polite"
		className="dp-fade fixed inset-0 z-[70] flex flex-col items-center justify-center px-8 text-center text-white bg-[linear-gradient(165deg,#5B4BFF,#2E2AE0_55%,#1B18A8)]"
		style={DRIVE_FONT_STYLE}
	>
		{state === 'processing' ? (
			<>
				<div className="dp-spin h-[76px] w-[76px] rounded-full border-4 border-white/20 border-t-white" />
				<div className="mt-7 text-base font-[700] tracking-[-0.015em]">Paying ₹{amount}</div>
				<div className="mt-2 max-w-[250px] text-[13px] text-[#CFCBFF]">Hold on, don&apos;t close this screen</div>
			</>
		) : (
			<>
				<div className="relative grid h-24 w-24 place-items-center">
					<div className="dp-ring absolute inset-0 rounded-full bg-white/[.16]" />
					<div className="dp-pulse absolute inset-0 rounded-full border-2 border-white/50" />
					<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" className="relative h-[52px] w-[52px]">
						<path d="M20 6 9 17l-5-5" style={{ strokeDasharray: 34, strokeDashoffset: 34, animation: 'dp-draw .4s cubic-bezier(.32,.72,0,1) .28s forwards' }} />
					</svg>
				</div>
				<div className="dp-rise mt-6" style={{ animationDelay: '0.42s' }}>
					<div className="text-[13px] font-[600] text-[#CFCBFF]">Payment successful</div>
					<div className="mt-1.5 text-[46px] font-[800] leading-none tracking-[-0.045em]">₹{formatRupees(amount)}</div>
				</div>
				<div className="dp-rise mt-4 max-w-[260px] text-[13px] text-[#CFCBFF]" style={{ animationDelay: '0.42s' }}>
					Seat confirmed for {dayjs(driveDate).format('D MMM')}
				</div>
			</>
		)}
	</div>
);

// The pass deliberately shows no amount: with coupons, users pay different prices and could compare them.
const getSeatFeeText = (registrationPayment: { isEnabled: boolean }) => {
	if (!registrationPayment.isEnabled) return { feeTitle: <>Free drive</>, feeNote: 'No payment needed.' };
	return { feeTitle: <>Seat deposit paid</>, feeNote: "Refund, if eligible, as per the drive's conditions." };
};

const Shortlisted = ({ data }: { data: GetMyDriveStatusAPIResponse }) => {
	const { drive } = data;
	const Razorpay = useRazorpay();
	const { user } = useUserContext();
	const toast = useToast();
	const queryClient = useQueryClient();
	const shortlistedBy = data.applications.filter(position => position.applicationStatus === 'shortlisted').map(position => position.company.name);
	const { isEnabled, amount } = drive.registrationPayment;

	const refresh = () => queryClient.invalidateQueries(['getMyDriveStatus', drive._id]);
	const onError = (error: Error) => toast({ type: 'error', title: get(error, ['response', 'data', 'message'], error.message) });

	const [paymentDone, setPaymentDone] = React.useState(false);
	const completeFn = useMutation(completeDrivePayment, {
		onSuccess() {
			toast({ type: 'success', title: 'Payment complete. Your seat is confirmed.' });
			// Let the success state play before the page flips to the entry pass.
			setPaymentDone(true);
			setTimeout(() => {
				setPaymentDone(false);
				refresh();
			}, 2600);
		},
		onError,
	});
	const cancelFn = useMutation(cancelDrivePayment, {
		onSuccess() {
			toast({ type: 'error', title: 'Payment failed.' });
		},
	});
	const createFn = useMutation(createDrivePayment, {
		onSuccess(result) {
			if (result.isFree || !result.payment) return refresh();
			const paymentId = result.payment._id;
			const options: RazorpayOptions = {
				key: RAZORPAY_APP_ID,
				amount: String(result.payment.amount),
				currency: 'INR',
				name: 'LevelUp',
				description: `Seat for ${drive.name}`,
				image: `${APP_ENDPOINT}/logo-razorpay.svg`,
				order_id: result.payment.orderId || '',
				handler: response =>
					completeFn.mutate({
						driveId: drive._id,
						paymentId,
						razorpay_payment_id: response.razorpay_payment_id,
						razorpay_order_id: response.razorpay_order_id,
						razorpay_signature: response.razorpay_signature,
					}),
				prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
				theme: { color: '#3027D8' },
			};
			const rzp = new Razorpay(options);
			rzp.on('payment.failed', () => cancelFn.mutate({ paymentId }));
			rzp.open();
		},
		onError,
	});
	const [couponInput, setCouponInput] = React.useState('');
	const [appliedCoupon, setAppliedCoupon] = React.useState<ValidateDriveCouponResponse | null>(null);
	const applyCouponFn = useMutation(validateDriveCoupon, {
		onSuccess(result) {
			setAppliedCoupon(result);
			toast({ type: 'success', title: `Coupon ${result.code} applied` });
		},
		onError(error: Error) {
			setAppliedCoupon(null);
			onError(error);
		},
	});
	const payable = appliedCoupon ? appliedCoupon.finalAmount : amount;
	const startPayment = () => createFn.mutate({ driveId: drive._id, couponCode: appliedCoupon?.code });
	const payLabel = isEnabled ? (payable >= 1 ? `Pay ₹${formatRupees(payable)}` : 'Confirm my seat') : 'Confirm my seat';
	const isBusy = createFn.isLoading || completeFn.isLoading || paymentDone;

	return (
		<div className="grid gap-8 lg:grid-cols-[1fr_380px]">
			<div>
				<span className="inline-flex items-center gap-2 px-3 py-1.5 lg:py-1 rounded-full bg-[#E6F6EC] text-[#1E7A44] text-[13px] font-[600]">
					<span className="h-2 w-2 rounded-full bg-[#2E9B5B]" />
					{shortlistedBy.join(', ')} shortlisted you
				</span>
				<h1 className="mt-3 text-[32px] leading-[36px] lg:text-[40px] lg:leading-[44px] tracking-[-0.03em] font-[800] text-[#111E2C] dark:text-white">You&apos;re in for {drive.name}</h1>
				<p className="mt-2 text-[16px] text-[#555770]">
					{dayjs(drive.driveDate).format('D MMMM, h:mm A')} · {drive.location}
				</p>
				<div className="mt-6">
					<ApplicationList applications={data.applications} />
				</div>
				<p className="hidden lg:block mt-4 rounded-[16px] bg-[#F6F6FA] dark:bg-[#1F2937] p-5 text-[15px] text-[#555770]">
					More companies may still shortlist you before {dayjs(drive.applicationCloseDate).format('D MMM')}. Anyone who does joins your day at no extra cost.
				</p>
			</div>
			<aside className="h-fit rounded-[20px] border border-[#EEEEF5] bg-gradient-to-b from-white to-[#F8F8FC] lg:bg-none lg:bg-white dark:bg-[#1F2937] p-5 lg:p-6 shadow-[0_2px_12px_rgba(20,20,60,0.06)]">
				{isEnabled ? (
					<>
						<p className="text-[13px] text-[#8A8BA3]">Seat deposit</p>
						<p className="text-[40px] lg:text-[44px] font-[800] text-[#111E2C] dark:text-white">
							₹{formatRupees(payable)}
							{appliedCoupon ? <span className="ml-2 text-[18px] font-[600] text-[#8A8BA3] line-through">₹{formatRupees(amount)}</span> : null}
						</p>
						<p className="text-[16px] font-[600] text-[#555770] pb-4 border-b border-[#EEEEF5]">
							Blocks your seat for the whole day, not charged per company
						</p>
						<div className="mt-4">
							<p className="text-[13px] text-[#8A8BA3]">Have a coupon code?</p>
							{appliedCoupon ? (
								<p className="mt-1 flex items-center justify-between rounded-[12px] bg-[#E6F6EC] px-3 py-2 text-[14px] font-[600] text-[#1E7A44]">
									<span>{appliedCoupon.code} applied ({appliedCoupon.discountPercentage}% off)</span>
									<button
										type="button"
										className="underline"
										disabled={isBusy}
										onClick={() => {
											setAppliedCoupon(null);
											setCouponInput('');
										}}
									>
										Remove
									</button>
								</p>
							) : (
								<form
									className="mt-1 flex gap-2"
									onSubmit={e => {
										e.preventDefault();
										if (couponInput.trim()) applyCouponFn.mutate({ driveId: drive._id, couponCode: couponInput.trim() });
									}}
								>
									<input
										value={couponInput}
										onChange={e => setCouponInput(e.target.value.toUpperCase())}
										placeholder="Enter code"
										className="min-w-0 flex-1 rounded-[12px] border border-[#DADAE6] bg-white px-3 py-2 text-[15px] uppercase text-[#111E2C]"
									/>
									<button
										type="submit"
										disabled={!couponInput.trim() || applyCouponFn.isLoading}
										className="rounded-[12px] border border-[#3027D8] px-4 py-2 text-[14px] font-[600] text-[#3027D8] disabled:opacity-50"
									>
										{applyCouponFn.isLoading ? '...' : 'Apply'}
									</button>
								</form>
							)}
						</div>
						{["Eligible for a refund as per the drive's refund conditions", 'No extra charge for more shortlists that day'].map(line => (
							<p key={line} className="mt-3 first:mt-4 flex items-start gap-3 text-[15px] text-[#555770]">
								<HiCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#2E9B5B]" />
								{line}
							</p>
						))}
					</>
				) : (
					<p className="text-[16px] font-[600] text-[#555770] pb-4 border-b border-[#EEEEF5]">This drive is free. Confirm your seat to get your entry pass.</p>
				)}
				<PrimaryButton className="hidden lg:block w-full mt-4" disabled={isBusy} onClick={startPayment}>
					{isBusy ? 'Please wait...' : payLabel}
				</PrimaryButton>
			</aside>
			<p className="lg:hidden rounded-[16px] bg-[#F6F6FA] dark:bg-[#1F2937] p-5 text-[15px] text-[#555770]">
				More companies may still shortlist you before {dayjs(drive.applicationCloseDate).format('D MMM')}. Anyone who does joins your day at no extra cost.
			</p>
			{completeFn.isLoading || paymentDone ? (
				<PaymentOverlay state={paymentDone ? 'done' : 'processing'} amount={payable} driveDate={drive.driveDate} />
			) : null}
			<MobileActionBar>
				<PrimaryButton className="w-full !py-4 !text-[17px]" disabled={isBusy} onClick={startPayment}>
					{isBusy ? 'Please wait...' : payLabel}
				</PrimaryButton>
			</MobileActionBar>
		</div>
	);
};

const Rejected = ({ data }: { data: GetMyDriveStatusAPIResponse }) => (
	<div>
		<h1 className="text-[32px] leading-[36px] lg:text-[40px] lg:leading-[44px] tracking-[-0.03em] font-[800] text-[#111E2C] dark:text-white">Not shortlisted this time</h1>
		<p className="mt-2 text-[16px] text-[#555770]">No company picked you this time.{data.drive.registrationPayment.isEnabled ? ' No money was taken.' : ''}</p>
		<div className="mt-6 max-w-[760px]">
			<ApplicationList applications={data.applications} />
		</div>
		{data.otherDrives.length ? (
			<section className="mt-10">
				<h2 className="text-[20px] font-[700] text-[#111E2C] dark:text-white">Still open to you</h2>
				<div className="mt-3 grid gap-4 grid-cols-1 lg:grid-cols-2">
					{data.otherDrives.map(drive => (
						<DriveCard key={drive._id} drive={drive} />
					))}
				</div>
			</section>
		) : null}
	</div>
);

const outlineButton =
	'inline-flex items-center justify-center gap-2 rounded-[12px] border border-[#E6E6EE] bg-white px-4 py-2 text-[14px] font-[600] whitespace-nowrap text-center text-[#111E2C] hover:bg-[#FAFAFC]';

const Registered = ({ data }: { data: GetMyDriveStatusAPIResponse }) => {
	const navigate = useNavigate();
	const { user } = useUserContext();
	const { drive } = data;
	// Same base path the router uses (see App.tsx): the app is served under /jobseeker everywhere except local dev.
	const basePath = process.env.NODE_ENV === 'development' ? '' : '/jobseeker';
	const attendanceUrl = `${window.location.origin}${basePath}/offline-drive-attendance?driveId=${drive._id}`;
	const meeting = data.applications.filter(position => position.applicationStatus === 'shortlisted').map(position => position.company.name);
	// The QR is for drive day: the venue shows big banners with QR codes to scan, so hide ours until then.
	const isDriveDay = !dayjs().isBefore(dayjs(drive.driveDate).startOf('day'));
	const driveDateLabel = dayjs(drive.driveDate).format('D MMMM');
	const meetingText = meeting.join(' · ');
	// Many companies, or names too long for the row: show the first one and send the candidate to the full list.
	const meetingIsLong = meeting.length > 2 || meetingText.length > 36;
	const meetingValue: React.ReactNode = !meeting.length ? (
		'-'
	) : meetingIsLong ? (
		<span className="inline-flex max-w-full items-center justify-end gap-2">
			<span className="max-w-[170px] truncate" title={meetingText}>
				{meeting[0]}
			</span>
			<button
				type="button"
				onClick={() => navigate(`/placement-drives/${drive._id}/companies`)}
				className="shrink-0 rounded-full bg-[#EEEDFD] px-2.5 py-0.5 text-[12px] font-[700] text-[#3730A3] hover:bg-[#E2E0FB]"
			>
				{meeting.length > 1 ? `+${meeting.length - 1} more` : 'View'}
			</button>
		</span>
	) : (
		meetingText
	);
	const rows: Array<[string, React.ReactNode]> = [
		['Reporting', dayjs(drive.driveDate).format('h:mm A')],
		['Venue', drive.location],
		['Meeting', meetingValue],
	];
	const calendarStart = dayjs(drive.driveDate);
	const calendarUrl =
		'https://calendar.google.com/calendar/render?action=TEMPLATE' +
		`&text=${encodeURIComponent(drive.name)}` +
		`&dates=${calendarStart.format('YYYYMMDD[T]HHmmss')}/${calendarStart.add(8, 'hour').format('YYYYMMDD[T]HHmmss')}` +
		`&location=${encodeURIComponent(drive.location)}`;
	const directionsUrl = drive.mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(drive.location)}`;

	const { feeTitle, feeNote } = getSeatFeeText(drive.registrationPayment);

	const desktop = (
		<div className="text-center">
			<h1 className="text-[34px] leading-[38px] md:text-[38px] md:leading-[42px] font-[800] tracking-[-0.03em] text-[#0F1626] dark:text-white">Seat confirmed</h1>
			<p className="mt-2 text-[15px] text-[#555770]">
				{isDriveDay ? `Show this pass at the entry gate on ${driveDateLabel}` : `Your entry pass unlocks on ${driveDateLabel}`}
			</p>

			<div className="mt-7 rounded-[28px] bg-[#F4F4F9] max-w-[1180px] mx-auto dark:bg-[#1F2937] p-0 md:p-5">
				<div className="flex flex-col md:flex-row rounded-[22px] overflow-hidden bg-white dark:bg-[#111827] text-left shadow-none md:shadow-[0_2px_10px_rgba(20,20,60,0.04)]">
					<div className="relative md:w-[30%] md:min-w-[220px] bg-gradient-to-b from-[#5A4FF0] to-[#2A22D2] text-white px-6 py-6 flex flex-col items-center border-b md:border-b-0 md:border-r-2 border-dashed border-[#8F89F5]">
						<p className="text-[13px] tracking-[0.18em] font-[700] text-[#D7D4FF]">ENTRY PASS</p>
						{isDriveDay ? (
							<>
								<div className="mt-4 w-full max-w-[170px] aspect-square rounded-[16px] bg-white p-3 flex items-center justify-center">
									<QRCodeSVG value={attendanceUrl} size={512} style={{ width: '100%', height: '100%' }} />
								</div>
								<a
									href={attendanceUrl}
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-flex items-center gap-1.5 rounded-[10px] bg-white/15 border border-white/40 px-4 py-1.5 text-[13px] font-[600] text-white hover:bg-white/25"
								>
									<HiOutlineExternalLink className="h-[18px] w-[18px] shrink-0" />
									Open link
								</a>
							</>
						) : (
							<div className="mt-4 flex w-full max-w-[200px] flex-col items-center rounded-[16px] border border-white/25 bg-white/10 px-4 py-6 text-center">
								<span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
									<HiOutlineLockClosed className="h-6 w-6" />
								</span>
								<p className="mt-3 text-[15px] font-[800]">
									QR unlocks on
									<span className="mt-0.5 block whitespace-nowrap text-[18px]">{driveDateLabel}</span>
								</p>
								<p className="mt-1 text-[12px] leading-[18px] text-[#D7D4FF]">On drive day, scan the QR on the venue banners to continue.</p>
							</div>
						)}
						<div className="mt-4 text-center">
							<p className="text-[20px] font-[700] tracking-[-0.01em]">{user?.name}</p>
							{data.userCode ? <p className="mt-1 text-[13px] text-[#C9C5FF]">{data.userCode}</p> : null}
						</div>
						<span className="hidden md:block absolute z-10 -right-3 -top-3 w-6 h-6 rounded-full bg-[#F4F4F9] dark:bg-[#1F2937]" />
						<span className="hidden md:block absolute z-10 -right-3 -bottom-3 w-6 h-6 rounded-full bg-[#F4F4F9] dark:bg-[#1F2937]" />
					</div>

					<div className="flex-1 px-6 md:px-8 py-6 flex flex-col">
						<div className="flex items-center justify-between gap-4 pb-5 border-b border-[#EEEEF4]">
							<h2 className="text-[22px] font-[700] tracking-[-0.02em] text-[#0F1626] dark:text-white">{drive.name}</h2>
							<DateBadge date={drive.driveDate} endDate={drive.driveEndDate} />
						</div>
						<dl className="py-5 space-y-4 border-b border-[#EEEEF4]">
							{rows.map(([label, value]) => (
								<div key={label} className="flex justify-between gap-6 text-[15px]">
									<dt className="text-[#6B6D85]">{label}</dt>
									<dd className="min-w-0 font-[600] text-[#0F1626] dark:text-white text-right">{value}</dd>
								</div>
							))}
						</dl>
						<div className="pt-5 flex items-center justify-between gap-4 flex-wrap mt-auto">
							<div>
								<p className="text-[16px] font-[700] text-[#0F1626] dark:text-white">
									{feeTitle}
								</p>
								<p className="mt-1 text-[13px] text-[#6B6D85]">
									{feeNote}
								</p>
							</div>
							<div className="flex items-center gap-3 flex-wrap">
								<a href={calendarUrl} target="_blank" rel="noreferrer" className={outlineButton}>
									<HiOutlineCalendar className="h-[18px] w-[18px] shrink-0" />
									Add to calendar
								</a>
								<a href={directionsUrl} target="_blank" rel="noreferrer" className={outlineButton}>
									<HiOutlineLocationMarker className="h-[18px] w-[18px] shrink-0" />
									Directions
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6">
				<button onClick={() => navigate(`/placement-drives/${drive._id}/companies`)} className={`${outlineButton} !px-6 !py-3 !text-[15px] w-full md:w-auto`}>
					<HiOutlineBriefcase className="h-[18px] w-[18px] shrink-0" />
					View my opportunities
				</button>
				<button onClick={() => navigate('/my-applications')} className="text-[15px] font-[500] text-[#6B6D85] whitespace-nowrap">
					Back to drives
				</button>
			</div>
		</div>
	);

	const mobile = (
		<div className="text-center">
			<h1 className="text-[30px] leading-[34px] font-[800] tracking-[-0.03em] text-[#0F1626] dark:text-white">Seat confirmed</h1>
			<p className="mt-1 text-[15px] text-[#555770]">{isDriveDay ? 'Show this at the entry gate' : `Your entry pass unlocks on ${driveDateLabel}`}</p>

			<div className="mt-5 rounded-[24px] bg-[#F4F4F9] dark:bg-[#1F2937] p-0">
				<div className="rounded-[22px] overflow-hidden bg-white dark:bg-[#111827] text-left">
					<div className="bg-gradient-to-r from-[#5A4FF0] to-[#2F28D6] text-white px-5 py-4 flex items-center justify-between gap-3">
						<div className="min-w-0">
							<p className="text-[12px] tracking-[0.16em] font-[700] text-[#D7D4FF]">ENTRY PASS</p>
							<TapToExpandText text={drive.name} className="mt-1 text-[20px] font-[700] tracking-[-0.01em]" />
						</div>
						<div className="w-[56px] h-[58px] rounded-[14px] bg-white/20 border border-white/30 flex flex-col items-center justify-center shrink-0">
							<span className="text-[20px] font-[700] leading-[22px]">{dayjs(drive.driveDate).format('DD')}</span>
							<span className="text-[11px] font-[700] tracking-wide">{dayjs(drive.driveDate).format('MMM').toUpperCase()}</span>
						</div>
					</div>

					<div className="px-5 pt-6 flex flex-col items-center">
						{isDriveDay ? (
							<>
								<div className="w-[170px] aspect-square rounded-[18px] border border-[#E6E6EE] bg-white p-3">
									<QRCodeSVG value={attendanceUrl} size={512} style={{ width: '100%', height: '100%' }} />
								</div>
								<a href={attendanceUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-[600] text-[#3730A3] underline underline-offset-2">
									<HiOutlineExternalLink className="h-[18px] w-[18px] shrink-0" />
									Open link
								</a>
							</>
						) : (
							<div className="flex w-full max-w-[260px] flex-col items-center rounded-[18px] border border-[#E6E6EE] bg-[#F6F6FA] px-5 py-7 text-center">
								<span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#4F46E5] shadow-[0_1px_3px_rgba(20,20,60,0.1)]">
									<HiOutlineLockClosed className="h-6 w-6" />
								</span>
								<p className="mt-3 text-[15px] font-[800] text-[#0F1626]">
									QR unlocks on
									<span className="mt-0.5 block whitespace-nowrap text-[18px]">{driveDateLabel}</span>
								</p>
								<p className="mt-1 text-[13px] leading-[20px] text-[#6B6D85]">On drive day, scan the QR on the venue banners to continue.</p>
							</div>
						)}
						<p className="mt-3 text-[20px] font-[700] text-[#0F1626] dark:text-white">{user?.name}</p>
						{data.userCode ? <p className="mt-0.5 text-[13px] text-[#8A8BA3]">{data.userCode}</p> : null}
					</div>

					<div className="relative my-5">
						<div className="border-t-2 border-dashed border-[#E3E3EC] mx-6" />
						<span className="absolute -left-3 -top-3 w-6 h-6 rounded-full bg-[#f7f9fb] dark:bg-[#1F2937]" />
						<span className="absolute -right-3 -top-3 w-6 h-6 rounded-full bg-[#f7f9fb] dark:bg-[#1F2937]" />
					</div>

					<dl className="px-6 pb-6 space-y-4">
						{rows.map(([label, value]) => (
							<div key={label} className="flex justify-between gap-4 text-[15px]">
								<dt className="text-[#6B6D85]">{label}</dt>
								<dd className="min-w-0 font-[600] text-[#0F1626] dark:text-white text-right">{value}</dd>
							</div>
						))}
					</dl>
				</div>
			</div>

			<div className="mt-4 grid grid-cols-2 gap-3">
				<a href={calendarUrl} target="_blank" rel="noreferrer" className={`${outlineButton} !px-2`}>
					<HiOutlineCalendar className="h-[18px] w-[18px] shrink-0" />
					Add to calendar
				</a>
				<a href={directionsUrl} target="_blank" rel="noreferrer" className={`${outlineButton} !px-2`}>
					<HiOutlineLocationMarker className="h-[18px] w-[18px] shrink-0" />
					Directions
				</a>
			</div>

			<div className="mt-4 rounded-[18px] bg-[#F4F4F9] dark:bg-[#1F2937] px-5 py-4 flex gap-3 text-left">
				<HiCheck className="mt-1 text-[#2E8B57] shrink-0" size={20} />
				<div>
					<p className="text-[16px] font-[700] text-[#0F1626] dark:text-white">{feeTitle}</p>
					<p className="mt-0.5 text-[14px] text-[#6B6D85]">{feeNote}</p>
				</div>
			</div>

			<button onClick={() => navigate(`/placement-drives/${drive._id}/companies`)} className={`${outlineButton} mt-8 w-full !py-3.5 !text-[15px]`}>
				<HiOutlineBriefcase className="h-[18px] w-[18px] shrink-0" />
				View my opportunities
			</button>
			<button onClick={() => navigate('/my-applications')} className="mt-4 mb-2 text-[15px] font-[500] text-[#6B6D85]">
				Back to drives
			</button>
		</div>
	);

	return (
		<>
			<div className="hidden md:block">{desktop}</div>
			<div className="md:hidden">{mobile}</div>
		</>
	);
};

const PlacementDriveStatusPage = () => {
	useDriveFont();
	const { driveId } = useParams();
	const navigate = useNavigate();
	const { data, isLoading } = useGetMyDriveStatus(driveId);

	if (isLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spinner size="xl" />
			</div>
		);
	}
	if (!data?.drive) return <div className="py-24 text-center text-[#555770]">Drive not found.</div>;
	if (data.stage === 'not_applied') return <Navigate to={`/placement-drives/${driveId}`} replace />;

	return (
		<Container className="dp-fade w-[90%] max-w-[1280px] pt-0 lg:pt-8 pb-32 lg:pb-24" style={DRIVE_FONT_STYLE}>
			<MobileHeader title={data.drive.name} onBack={() => navigate('/my-applications')} />
			{data.stage === 'under_review' ? <UnderReview data={data} /> : null}
			{data.stage === 'shortlisted' ? <Shortlisted data={data} /> : null}
			{data.stage === 'rejected' ? <Rejected data={data} /> : null}
			{data.stage === 'registered' ? <Registered data={data} /> : null}
		</Container>
	);
};

export default PlacementDriveStatusPage;
